// @ts-strict-ignore
import {Component, Input, OnChanges, OnDestroy, OnInit} from "@angular/core";
import {ActivatedRoute} from "@angular/router";
import {TranslateService} from "@ngx-translate/core";
import * as Chart from "chart.js";
import {AbstractHistoryChart} from "src/app/edge/history/abstracthistorychart";
import {AbstractHistoryChart as NewAbstractHistoryChart} from "src/app/shared/components/chart/abstracthistorychart";
import {ChartConstants} from "src/app/shared/components/chart/chart.constants";
import {ComponentJsonApiRequest} from "src/app/shared/jsonrpc/request/componentJsonApiRequest";
import {ChannelAddress, Currency, Edge, EdgeConfig, Service, Websocket} from "src/app/shared/shared";
import {ColorUtils} from "src/app/shared/utils/color/color.utils";
import {ChartAxis, HistoryUtils, TimeOfUseTariffUtils, YAxisType} from "src/app/shared/utils/utils";
import {GetTimeOfUseDataResponse} from "../../../../../../shared/jsonrpc/response/getTimeOfUseDataResponse";
import {GetTimeOfUseDataRequest} from "../../../../../../shared/jsonrpc/request/getTimeOfUseDataRequest";
import {filter, take} from "rxjs/operators";
import {Formatter} from "../../../../../../shared/components/shared/formatter";

@Component({
  selector: "timeOfUseTariffPriceChart",
  templateUrl: "../../../../../history/abstracthistorychart.html",
  standalone: false,
})
export class TimeOfUseTariffPriceChartComponent extends AbstractHistoryChart implements OnInit, OnChanges, OnDestroy {

  @Input({required: true}) public refresh!: boolean;
  @Input({required: true}) public override edge!: Edge;
  @Input({required: true}) public component!: EdgeConfig.Component;

  private currencyLabel: Currency.Label = Currency.Label.CENT_PER_KWH;
  private currencyUnit: Currency.Unit; // Default

  constructor(
    protected override service: Service,
    protected override translate: TranslateService,
    private route: ActivatedRoute,
    private websocket: Websocket,
  ) {
    super("timeOfUseTariffPrice-chart", service, translate);
  }

  public async ngOnChanges() {
    this.edge.getConfig(this.websocket).pipe(filter(config => !!config), take(1)).subscribe(config => {
      const meta: EdgeConfig.Component = config?.getComponent("_meta");
      const currency: string = config?.getPropertyFromComponent<string>(meta, "currency");
      this.currencyLabel = Currency.getCurrencyLabelByCurrency(currency);
      this.currencyUnit = Currency.getChartCurrencyUnitLabel(currency);
    });
    this.updateChart();
  }

  public ngOnInit() {
    this.service.startSpinner(this.spinnerId);
    const config = this.edge.getCurrentConfig();
    if (config) {
      const currency = config.getComponent("Core.Meta")?.properties["currency"];
      this.currencyLabel = Currency.getCurrencyLabelByCurrency(currency);
    }
  }

  public ngOnDestroy() {
    this.unsubscribeChartRefresh();
  }

  public getChartHeight(): number {
    return TimeOfUseTariffUtils.getChartHeight(this.service.isSmartphoneResolution);
  }

  protected setLabel() {
    this.options = this.createDefaultChartOptions();
  }

  protected getChannelAddresses(): Promise<ChannelAddress[]> {
    return new Promise(() => {
      [];
    });
  }

  protected override updateChart() {

    this.autoSubscribeChartRefresh();
    this.service.startSpinner(this.spinnerId);
    this.loading = true;

    this.edge.sendRequest(
      this.websocket,
      new ComponentJsonApiRequest({componentId: this.component.id, payload: new GetTimeOfUseDataRequest()}),
    ).then(response => {
      const result = (response as GetTimeOfUseDataResponse).result;
      const datasets = [];

      const consumption = result.consumption;
      const production = result.production;

      const consumptionArray = consumption.map(entry => TimeOfUseTariffUtils.formatPrice(entry.price));
      const productionArray = production.map(entry => TimeOfUseTariffUtils.formatPrice(entry.price));
      const labels = consumption.map(entry => new Date(entry.timestamp));

      datasets.push({
        type: "bar",
        label: this.translate.instant("GENERAL.GRID_BUY_PRICE"),
        data: consumptionArray,
        hidden: false,
        order: 1,
        stack: "consumption",
      });
      this.colors.push({
        backgroundColor: ColorUtils.rgbStringToRgba(ChartConstants.Colors.YELLOW, 1),
        borderColor: ChartConstants.Colors.YELLOW,
      });

      datasets.push({
        type: "bar",
        label: this.translate.instant("GENERAL.GRID_SELL_PRICE"),
        data: productionArray,
        hidden: false,
        order: 1,
        stack: "production",
      });
      this.colors.push({
        backgroundColor: ColorUtils.rgbStringToRgba(ChartConstants.Colors.BLUE, 1),
        borderColor: ChartConstants.Colors.BLUE,
      });

      this.datasets = datasets;
      this.loading = false;
      this.labels = labels;
      this.setLabel();
      this.stopSpinner();
    }).catch((reason) => {
      console.error(reason);
      this.initializeChart();
      return;
    }).finally(async () => {
      await this.setOptions(this.options);
      this.applyControllerSpecificOptions();
    });
  }

  private applyControllerSpecificOptions() {
    const leftYAxis: HistoryUtils.yAxes = {
      position: "left",
      unit: YAxisType.CURRENCY,
      yAxisId: ChartAxis.LEFT,
      customTitle: this.currencyUnit,
    };
    this.options = NewAbstractHistoryChart.getYAxisOptions(this.options, leftYAxis, this.translate, "bar", this.datasets, true);

    this.options.scales.x["ticks"] = {source: "auto", autoSkip: false};
    this.options.scales.x["stacked"] = false;
    this.options.scales.y["stacked"] = false;

    this.options.scales.x.ticks.color = getComputedStyle(document.documentElement).getPropertyValue("--ion-color-chart-xAxis-ticks");
    this.options.scales.x.ticks.callback = function (value, index, values) {
      const date = new Date(value);

      // Display the label only if the minutes are zero (full hour)
      return date.getMinutes() === 0 ? date.getHours() + ":00" : "";
    };

    this.options.plugins.tooltip.callbacks.label = (item: Chart.TooltipItem<any>) => {

      const label = item.dataset.label;
      const value = item.dataset.data[item.dataIndex];

      return label + ": " + Formatter.FORMAT_CURRENCY_PER_KWH(value, this.currencyLabel);
    };

    this.options.scales[ChartAxis.LEFT].suggestedMin = 0;
  }

}
